import { CardContent, Stack, useTheme } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Card, CbhIcon, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useInView } from "react-intersection-observer";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { GetAJobModal } from "./GetAJobModal";

interface SignOnBonusBannerProps {
  placementId: string;
  placementCandidate: PlacementCandidate;
}

export function SignOnBonusBanner(props: SignOnBonusBannerProps) {
  const { placementId, placementCandidate } = props;
  const theme = useTheme();
  const getAJobModalState = useModalState();
  const [isHidden, setIsHidden] = useLocalStorage("placement-sign-on-bonus-banner-hidden", false);
  const { placementClaimSignOnBonusSupportLink } = useSupportLinks();

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_SIGN_ON_BONUS_BANNER_VIEWED, {
        placementId,
        placementCandidateId: placementCandidate.id,
      });
    },
  });

  return isHidden ? null : (
    <>
      <Card
        ref={ref}
        sx={{
          p: 1.5,
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          background: `linear-gradient(90deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.light}60 100%)`,
          textAlign: "center",
          position: "relative",
        }}
      >
        <CbhIcon
          type="close"
          sx={{ cursor: "pointer", position: "absolute", right: 12, top: 12 }}
          onClick={() => {
            setIsHidden(true);
          }}
        />
        <CardContent component={Stack} spacing={5} sx={{ alignItems: "center" }}>
          <Stack spacing={3} sx={{ alignItems: "center" }}>
            <Text semibold variant="h5" sx={{ fontWeight: "bold" }}>
              Got the Job? Get Your Gift! 🎁
            </Text>

            <Text
              variant="body1"
              sx={{ maxWidth: "90%", alignSelf: "center", color: theme.palette.text.secondary }}
            >
              When you start your first job through Clipboard, we&apos;ll send you a special gift to
              celebrate!
            </Text>
          </Stack>
          <Button
            invert
            variant="contained"
            onClick={() => {
              getAJobModalState.openModal();
            }}
          >
            Claim My Gift
          </Button>
          <TextButton href={placementClaimSignOnBonusSupportLink}>Learn about the gift</TextButton>
        </CardContent>
      </Card>
      {getAJobModalState.modalIsOpen ? (
        <GetAJobModal
          modalState={getAJobModalState}
          placementCandidate={placementCandidate}
          placementId={placementId}
        />
      ) : undefined}
    </>
  );
}
